import { markets } from '../../data/mock';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Shield, MapPin } from 'lucide-react';

const MarketCard = ({ market, index }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <div
      ref={ref}
      className="group relative overflow-hidden cursor-pointer"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${index * 150}ms`,
      }}
    >
      <div className="aspect-[3/4] md:aspect-[4/5] overflow-hidden">
        <img
          src={market.image}
          alt={market.region}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/90 via-[#0A0A0A]/30 to-transparent" />
      </div>

      {/* Hurricane badge */}
      {market.badge && (
        <div className="absolute top-5 right-5 flex items-center gap-1.5 bg-[#C4A265] px-3 py-1.5">
          <Shield size={12} className="text-[#0A0A0A]" />
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#0A0A0A] font-medium">
            {market.badge}
          </span>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
        <h3 className="font-serif text-3xl md:text-4xl text-[#F5F1EC] italic mb-2">
          {market.region}
        </h3>
        <div className="flex items-start gap-2 mb-4">
          <MapPin size={13} className="text-[#C4A265] mt-0.5 shrink-0" />
          <p className="text-[#C4A265] text-[11px] tracking-[0.15em] uppercase">
            {market.locations}
          </p>
        </div>
        <p className="text-[#9A9590] text-sm leading-relaxed max-w-sm opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500">
          {market.description}
        </p>
      </div>
    </div>
  );
};

export const MarketsSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation();

  return (
    <section id="markets" className="bg-[#0A0A0A] py-20 lg:py-28 border-t border-white/[0.03]">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-16">
        <div
          ref={headerRef}
          className={`mb-14 lg:mb-18 transition-all duration-700 ${
            headerVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-[#C4A265] tracking-[0.4em] uppercase text-[11px] mb-3">
            Coast to Coast
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-[#F5F1EC] leading-[1.05] mb-4">
            <span className="font-light">Our</span>{' '}
            <span className="italic">Markets</span>
          </h2>
          <p className="text-[#8A8580] text-base max-w-lg">
            Serving the East Coast, West Coast, and Florida with custom steel,
            bronze, and aluminum solutions — including hurricane-rated systems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {markets.map((market, index) => (
            <MarketCard key={market.id} market={market} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
